import { cn } from "@/lib/utils";
import { formatMs } from "@/utils/format";
import type { TraceNode, TraceStatus } from "@/utils/trace/model";

// A bar's colour says what kind of work it is; a failed or still-running node
// overrides that, because "it broke" matters more than "it was a tool call".
const KIND_CLASS: Record<TraceNode["kind"], string> = {
  turn: "bg-trace-inference",
  tool_call: "bg-trace-tool",
  cw_span: "bg-trace-citation",
  // The envelope is a container, so it takes the pale step and the work laid
  // over it takes the solid one. Painting both solid made the model turns
  // invisible against the execution they sit inside.
  tool_batch: "bg-trace-tool-wait",
  // Delegation, job and execution are three nested containers of the same
  // sub-agent work, so they take the same pale step rather than three shades
  // nobody could tell apart. What marks the boundary between them is the wait
  // band on the job: where no execution was running, the job was queued.
  async_delegation: "bg-trace-subagent-wait",
  async_job: "bg-trace-subagent-wait",
  sa_execution: "bg-trace-subagent-wait",
  sa_model_turn: "bg-trace-subagent",
  sa_tool_call: "bg-trace-tool",
};

/** Containers. The work drawn inside them starts at the same moment. */
const ENVELOPE_KINDS = new Set<TraceNode["kind"]>([
  "sa_execution",
  "tool_batch",
  "async_delegation",
  "async_job",
]);

const STATUS_CLASS: Partial<Record<TraceStatus, string>> = {
  error: "bg-trace-error",
  running: "bg-trace-running",
  cancelled: "bg-trace-idle",
};

/** Below this share of the track a label cannot fit inside the bar. */
const INLINE_LABEL_MIN_FRACTION = 0.08;

// A 200ms tool call on a minute-long axis is 0.3% of the track — two or three
// pixels. Painting that is honest; making it the click target is not. The mark
// keeps its measured width and the hit box grows under it instead, centred so
// the target never moves out from under the pointer.
const HIT_MIN = "min-w-6"; // 24px, per the dataviz hit-target floor
const MARK_MIN = "min-w-[3px]"; // a hairline is invisible; this is the paint floor

export type TraceScale = {
  /** Fraction of the track, 0..1, for an absolute offset. */
  fractionOf: (ms: number) => number;
};

function pct(value: number) {
  return `${(value * 100).toFixed(3)}%`;
}

export function TraceBar({
  node,
  scale,
  selected,
  highlighted,
  onSelect,
}: {
  node: TraceNode;
  scale: TraceScale;
  selected: boolean;
  highlighted: boolean;
  onSelect: (node: TraceNode) => void;
}) {
  if (node.startMs === null) return null;

  // A node that started and never recorded an end runs to the edge of the axis.
  // Dropping it would hide a hung tool call — the single most interesting thing
  // a QA reviewer can find — so it is drawn open-ended instead.
  const openEnded = node.endMs === null;
  const left = scale.fractionOf(node.startMs);
  const right = openEnded ? 1 : scale.fractionOf(node.endMs!);
  // Keep zero-length nodes visible instead of collapsing them to nothing.
  const width = Math.max(right - left, 0.002);

  // A turn is the model thinking, and then whatever is left of the wait window
  // once its own tool calls are taken out — those are drawn as their own bars,
  // so painting the whole window as "waiting" would colour the same time twice.
  const inferenceEnd = node.inferenceEndMs;
  const hasSplit =
    node.kind === "turn" &&
    !openEnded &&
    inferenceEnd !== null &&
    inferenceEnd !== undefined &&
    inferenceEnd < node.endMs!;
  // A turn whose first tool call starts immediately is all wait, not all
  // inference — clamp rather than letting the split collapse to one segment.
  const inferenceWidth = hasSplit
    ? Math.min(Math.max((scale.fractionOf(inferenceEnd) - left) / width, 0), 1)
    : 1;
  // Not gated on `hasSplit`: a sub-agent execution has no inference point but
  // still pales out the windows its own tools held.
  //
  // An absent `waitSegments` means nobody worked them out, so a split turn
  // falls back to its whole wait window; an empty array means they were worked
  // out and nothing is left to pale. Those are different answers.
  const waitSource =
    node.waitSegments ??
    (hasSplit ? [{ startMs: inferenceEnd!, endMs: node.endMs! }] : []);
  // Positioned within the bar, so fractions are relative to its own width.
  const waitBands = waitSource.map((segment) => ({
    key: `${segment.startMs}-${segment.endMs}`,
    left: Math.min(Math.max((scale.fractionOf(segment.startMs) - left) / width, 0), 1),
    width: Math.min(
      Math.max(
        (scale.fractionOf(segment.endMs) - scale.fractionOf(segment.startMs)) / width,
        0,
      ),
      1,
    ),
  }));

  const fill = STATUS_CLASS[node.status] ?? KIND_CLASS[node.kind];
  const duration = openEnded ? "완료 기록 없음" : formatMs(node.durationMs);
  const title = [
    // The width is measured but the position is not, so the label says so
    // rather than letting the bar imply a time it was never recorded at.
    node.approximate ? `≈ ${node.label}` : node.label,
    node.rawStatus || null,
    duration,
    node.approximate ? "위치는 재구성값" : null,
    node.model ? `· ${node.model}` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  // The envelope skips its label: the first thing inside it starts at the same
  // moment and paints straight over the text, and the lane header a few pixels
  // above already carries the same name.
  const labelFits =
    width >= INLINE_LABEL_MIN_FRACTION && !ENVELOPE_KINDS.has(node.kind);

  return (
    // Three layers: this box is the measured geometry, the button is the hit
    // target that may reach past it, and the mark inside paints the time.
    <div
      data-bar
      style={{ left: pct(left), width: pct(width) }}
      className={cn("absolute inset-y-0", MARK_MIN)}
    >
      {/* `w-full` wins whenever the bar is already wider than the floor, so a
          long bar behaves exactly as before; a short one grows evenly to both
          sides. No track measurement needed — the browser resolves it. */}
      <button
        type="button"
        title={title}
        aria-label={title}
        aria-pressed={selected}
        onClick={() => onSelect(node)}
        className={cn(
          "peer absolute inset-y-0 left-1/2 w-full -translate-x-1/2 cursor-pointer",
          HIT_MIN,
        )}
      />
      {/* Sibling rather than child: inside the button it would be clipped back
          to the painted width, which is the whole problem. `peer-*` reaches it
          because it follows the button. */}
      <span
        data-mark
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 overflow-hidden rounded border",
          // A 2px surface ring so adjacent marks keep a readable boundary.
          "ring-2 ring-surface-subtle",
          selected
            ? "border-foreground"
            : node.approximate
              ? "border-dashed border-foreground/40"
              : "border-transparent",
          highlighted && !selected && "ring-foreground",
          // Which bar the click will land on, before it lands. Keyboard focus
          // gets the same answer as the pointer.
          !highlighted &&
            !selected &&
            "peer-hover:ring-foreground/60 peer-focus-visible:ring-foreground",
        )}
      >
        <span
          data-segment="inference"
          className={cn(
            "absolute inset-y-0 left-0",
            fill,
            // Hatched and fading out: the bar has a start but no measured end,
            // so its length must not read as a duration.
            openEnded &&
              "opacity-60 bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,rgb(255_255_255/40%)_3px,rgb(255_255_255/40%)_6px)]",
          )}
          style={{ width: pct(inferenceWidth) }}
        />
        {waitBands.map((band) => (
          <span
            key={band.key}
            data-segment="wait"
            className="absolute inset-y-0 bg-trace-wait"
            style={{ left: pct(band.left), width: pct(band.width) }}
          />
        ))}
        {node.finalizeAnswer && (
          // The citation barrier. Without this marker the turn reads as a tool
          // call that went missing.
          <span className="absolute inset-y-0 right-0 w-0.5 bg-foreground" />
        )}
        {node.status === "error" && (
          // Error crimson sits ΔE 6.0 from the running amber for deuteranopes —
          // inside the band that is only legal with a second channel, so the
          // colour never carries the failure on its own.
          <span className="absolute inset-y-0 left-0 flex w-2.5 items-center justify-center text-[10px] leading-none font-bold text-white">
            !
          </span>
        )}
        {labelFits && (
          // Clamped to the solid segment: on a split turn the right-hand wait
          // band is pale, and white text would disappear into it.
          <span
            className={cn(
              "absolute inset-y-0 flex items-center truncate pr-1 text-[11px] leading-none",
              node.status === "error" ? "left-2.5" : "left-0 pl-1",
              "text-white",
            )}
            style={{ maxWidth: pct(inferenceWidth) }}
          >
            {node.approximate ? `≈ ${node.label}` : node.label}
          </span>
        )}
      </span>
    </div>
  );
}
